/**
 * Module Intro Registry — "Wat leer je hier?"
 *
 * Part of the Content domain (DDD Fase 3).
 * Data-driven module introductions per group (groep 6/7/8).
 * Used in ModulePanel to show students what they'll learn.
 *
 * To add a new module: add an entry to MODULE_INTRO keyed by ModuleId.
 * Each entry has per-group intro data with topics and an optional tip.
 */

import { ModuleId } from './types';
import { GroupId } from '../config/systemConfig';
export interface ModuleIntroData {
  /** 3-5 short bullet points describing what the student will learn */
  topics: string[];
  /** Optional motivational or contextual tip */
  tip?: string;
}
const MODULE_INTRO: Record<ModuleId, Record<GroupId, ModuleIntroData>> = {
  Getalbegrip: {
    6: {
      topics: ['Grote getallen lezen en schrijven', 'Breuken herkennen en vergelijken', 'Kommagetallen begrijpen', 'Positiewaarde van cijfers'],
      tip: 'Getallen zijn de basis van alles in wiskunde!'
    },
    7: {
      topics: ['Procenten berekenen', 'Breuken vereenvoudigen en uitbreiden', 'Verhoudingen en verhoudingstabellen', 'Negatieve getallen'],
      tip: 'Procenten gebruik je overal: in de winkel, bij sport, en later op je werk.'
    },
    8: {
      topics: ['Procenten terugrekenen en BTW', 'Samengestelde procenten', 'Machten en wortels', 'Wetenschappelijke notatie', 'Kommagetallen vermenigvuldigen en delen']
    }
  },
  Basisrekenen: {
    6: {
      topics: ['Tafels van vermenigvuldiging automatiseren', 'Cijferend optellen en aftrekken', 'Cijferend vermenigvuldigen', 'Delen met rest'],
      tip: 'Als je de tafels goed kent, wordt al het andere rekenen makkelijker!'
    },
    7: {
      topics: ['Handig rekenen met strategieën', 'Contextopgaven (redactiesommen)', 'Gemengde bewerkingen', 'Schatten en afronden']
    },
    8: {
      topics: ['Meerstaps-opgaven oplossen', 'Volgorde van bewerkingen', 'Rekenen met haakjes', 'Schatten en controleren']
    }
  },
  Metriek: {
    6: {
      topics: ['Lengtematen omrekenen (mm, cm, m, km)', 'Gewicht meten en omrekenen (g, kg)', 'Inhoud meten (ml, cl, l)', 'Schatten van maten'],
      tip: 'Kijk eens om je heen — overal zie je maten en gewichten!'
    },
    7: {
      topics: ['Oppervlakte berekenen (m²)', 'Omtrek van figuren', 'Het metriek stelsel begrijpen', 'Hoeken meten en tekenen']
    },
    8: {
      topics: ['Inhoud en volume (m³, liter)', 'Schaalberekeningen op kaarten', 'Samengestelde figuren', 'Oppervlakte van driehoeken en cirkels']
    }
  },
  Tijd: {
    6: {
      topics: ['Analoog en digitaal klokkijken', 'Kwartieren en 5-minuten aflezen', 'Tijdsduur berekenen', 'Kalender en datum'],
      tip: 'Oefen met de klok thuis — kijk hoe laat het is en reken uit hoeveel minuten tot het eten!'
    },
    7: {
      topics: ['Tijdsduur berekenen over middernacht', "Schema's en roosters lezen", 'Tijdseenheden omrekenen', 'Reistijd en planning']
    },
    8: {
      topics: ['Snelheid berekenen (km/u)', 'Afstand en tijd combineren', 'Gemiddelde snelheid', 'Tijdzones begrijpen']
    }
  },
  Geld: {
    6: {
      topics: ['Munten en biljetten herkennen', 'Bedragen maken en tellen', 'Wisselgeld berekenen', 'Geld wisselen'],
      tip: 'Oefen in de winkel: hoeveel wisselgeld krijg je terug?'
    },
    7: {
      topics: ['Prijzen vergelijken', 'Boodschappen en budget', 'Korting berekenen', 'Prijs per eenheid']
    },
    8: {
      topics: ['BTW berekenen en terugrekenen', 'Rente en sparen', 'Budgetteren', 'Financieel rekenen']
    }
  },
  SpellingWW: {
    6: {
      topics: ['De stam van een werkwoord bepalen', 'Tegenwoordige tijd: ik, jij, hij/zij', 'Jij-omkering herkennen', 'Hele werkwoord vs. stam + t'],
      tip: "Onthoud: de stam is het hele werkwoord zonder -en. 'Werken' → 'werk'."
    },
    7: {
      topics: ["Verleden tijd met 't Kofschip", 'Sterke en zwakke werkwoorden', 'Voltooid deelwoord vormen', 'Onregelmatige werkwoorden'],
      tip: "'t Kofschip helpt je kiezen tussen -te/-de en -t/-d."
    },
    8: {
      topics: ["Lastige d's en t's", 'Bijvoeglijk gebruikt voltooid deelwoord', 'Gebiedende wijs', 'Alle werkwoordstijden combineren']
    }
  },
  SpellingRegel: {
    6: {
      topics: ['Lange en korte klanken schrijven', 'Verdubbeling en verkorting', 'Woorden met ei/ij en au/ou', 'Leestekens: punt, komma, vraagteken']
    },
    7: {
      topics: ['Weetwoorden en leenwoorden', 'Samenstellingen met tussen-n en -s', 'Trema en koppelteken', 'Hoofdlettergebruik']
    },
    8: {
      topics: ['Woordsoorten herkennen', 'Zinsontleding: onderwerp en gezegde', 'Lijdend en meewerkend voorwerp', 'Bijzinnen en voorzetsels']
    }
  },
  Taalbegrip: {
    6: {
      topics: ['Vragen beantwoorden over korte teksten', 'Woordbetekenis uit context afleiden', 'Kern van een tekst vinden', 'Synoniemen en antoniemen']
    },
    7: {
      topics: ['Verwijswoorden herkennen', 'Signaalwoorden begrijpen', 'Doel van de schrijver bepalen', 'Feit en mening onderscheiden']
    },
    8: {
      topics: ['Moeilijke woorden en idioom', 'Tekststructuur analyseren', 'Argumentatie herkennen', 'Samenvatten en conclusies trekken']
    }
  },
  Studievaardig: {
    6: {
      topics: ['Woordenboek gebruiken', 'Index en inhoudsopgave lezen', 'Alfabetische volgorde', 'Informatie opzoeken']
    },
    7: {
      topics: ['Kaarten en plattegronden lezen', "Schema's en tabellen interpreteren", 'Grafieken aflezen', 'Routes plannen']
    },
    8: {
      topics: ['Bronnen beoordelen op betrouwbaarheid', 'Informatie ordenen en samenvatten', "Diagrammen en stroomschema's", 'Notities maken']
    }
  },
  Topo: {
    6: {
      topics: ['Provincies van Nederland', 'Hoofdsteden van provincies', 'Rivieren en wateren', 'Buurlanden van Nederland']
    },
    7: {
      topics: ['Landen van Europa', 'Hoofdsteden van Europese landen', 'Zeeën en gebergten', 'Klimaatzones']
    },
    8: {
      topics: ['Werelddelen en oceanen', 'Grote landen en hoofdsteden', 'Rivieren en gebergten wereldwijd', 'Klimaat en landschap']
    }
  }
};

/**
 * Get the "Wat leer je hier?" intro data for a module and group.
 * Returns null if no data is registered (graceful fallback).
 */
export function getModuleIntro(moduleId: ModuleId, group: GroupId): ModuleIntroData | null {
  return MODULE_INTRO[moduleId]?.[group] ?? null;
}